// ImagePreviewModal Component - Full-screen image viewer with carousel and navigation
import React, { useState, useEffect } from 'react';
import '../styles/ImagePreviewModal.css';

const ImagePreviewModal = ({ images = [], initialIndex = 0, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isZoomed, setIsZoomed] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') handlePrevious();
      if (e.key === 'ArrowRight') handleNext();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, images.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
    setIsZoomed(false);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
    setIsZoomed(false);
  };

  const handleThumbnailClick = (index) => {
    setCurrentIndex(index);
    setIsZoomed(false);
  };

  const handleImageClick = () => {
    setIsZoomed(!isZoomed);
  };

  // Swipe gesture handlers
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    if (isLeftSwipe) {
      handleNext();
    } else if (isRightSwipe) {
      handlePrevious();
    }
  };

  if (!images || images.length === 0) return null;

  const currentImage = images[currentIndex];

  return (
    <div className="image-preview-modal" onClick={onClose}>
      <div className="image-preview-content" onClick={(e) => e.stopPropagation()}>
        {/* Close Button */}
        <button className="image-preview-close" onClick={onClose} aria-label="Close">
          <i className="fas fa-times"></i>
        </button>

        {/* Main Image Display */}
        <div className="image-preview-main">
          {images.length > 1 && (
            <button 
              className="image-preview-nav image-preview-nav-left" 
              onClick={handlePrevious}
              aria-label="Previous image"
            >
              <i className="fas fa-chevron-left"></i>
            </button>
          )}

          <div 
            className={`image-preview-image-container ${isZoomed ? 'zoomed' : ''}`}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            {currentImage.type?.startsWith('video/') ? (
              <video
                src={currentImage.url}
                className="image-preview-image"
                controls
                autoPlay
                loop
              />
            ) : (
              <img
                src={currentImage.url}
                alt={currentImage.name || `Image ${currentIndex + 1}`}
                className="image-preview-image"
                onClick={handleImageClick}
              />
            )}
          </div>

          {images.length > 1 && (
            <button 
              className="image-preview-nav image-preview-nav-right" 
              onClick={handleNext}
              aria-label="Next image"
            >
              <i className="fas fa-chevron-right"></i>
            </button>
          )}
        </div>

        {/* Image Info */}
        <div className="image-preview-info">
          <div className="image-preview-name">
            {currentImage.name || `Image ${currentIndex + 1} of ${images.length}`}
          </div>
          <div className="image-preview-counter">
            {currentIndex + 1} / {images.length}
          </div>
        </div>

        {/* Thumbnail Footer */}
        <div className="image-preview-thumbnails">
          <div className="image-preview-thumbnails-scroll">
            {images.map((image, index) => (
              <div
                key={index}
                className={`image-preview-thumbnail ${index === currentIndex ? 'active' : ''}`}
                onClick={() => handleThumbnailClick(index)}
              >
                {image.type?.startsWith('video/') ? (
                  <>
                    <video src={image.url} muted style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', color: 'white', fontSize: '18px', textShadow: '0 1px 4px rgba(0,0,0,0.6)' }}>
                      <i className="fas fa-play"></i>
                    </div>
                  </>
                ) : (
                  <img
                    src={image.url}
                    alt={image.name || `Thumbnail ${index + 1}`}
                  />
                )}
                {index === currentIndex && (
                  <div className="image-preview-thumbnail-active-indicator"></div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Download Button */}
        <a
          href={currentImage.url}
          download={currentImage.name || `image-${currentIndex + 1}`}
          className="image-preview-download"
          onClick={(e) => e.stopPropagation()}
        >
          <i className="fas fa-download"></i>
        </a>
      </div>
    </div>
  );
};

export default ImagePreviewModal;
